import { useEffect, useState } from 'react';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { Flags, useFeatureFlagsStore } from '../stores/featureFlagsStore';
import { errorReporting } from '../services/logging';
import { toast } from 'sonner';

export const useLaunchDarklySync = () => {
  const flags = useFlags<Flags>();
  const client = useLDClient();
  const { setFlags, setIsInitialized } = useFeatureFlagsStore();
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    try {
      if (flags && Object.keys(flags).length > 0) {
        setFlags(flags);
        setIsInitialized(true);
        setHasError(false);
      }
    } catch (error) {
      const errorMessage = 'Failed to sync LaunchDarkly flags';
      console.error(errorMessage, error);
      errorReporting.logError(error instanceof Error ? error : new Error(errorMessage));

      if (!hasError) {
        toast.error('Feature flags failed to load. Some features may be unavailable.');
        setHasError(true);
      }
    }
  }, [flags, setFlags, setIsInitialized, hasError]);

  useEffect(() => {
    if (client) {
      try {
        const handleFlagsChange = (flags: Record<string, any>) => {
          try {
            setFlags(flags);
            setHasError(false);
          } catch (error) {
            const errorMessage = 'Failed to update LaunchDarkly flags';
            console.error(errorMessage, error);
            errorReporting.logError(error instanceof Error ? error : new Error(errorMessage));

            if (!hasError) {
              toast.error('Feature flags update failed. Some features may be unavailable.');
              setHasError(true);
            }
          }
        };

        client.on('change', handleFlagsChange);

        return () => {
          try {
            client.off('change', handleFlagsChange);
          } catch (error) {
            console.warn('Failed to remove LaunchDarkly change listener:', error);
          }
        };
      } catch (error) {
        const errorMessage = 'Failed to set up LaunchDarkly change listener';
        console.error(errorMessage, error);
        errorReporting.logError(error instanceof Error ? error : new Error(errorMessage));

        if (!hasError) {
          toast.error('Feature flags failed to initialize. Some features may be unavailable.');
          setHasError(true);
        }
      }
    }
  }, [client, setFlags, hasError]);

  return { hasError };
};
